import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  Copy,
  FileText,
  Globe2,
  Info,
  Languages,
  LockKeyhole,
  MailCheck,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  XCircle
} from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { CheckLevel, ChecklistItem, Finding, MailAuthAudit } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type PolicyMode = "monitor" | "quarantine" | "reject";
type OutputKey = "dmarc" | "spf" | "mtaStsTxt" | "mtaStsPolicy" | "tlsRpt" | "bimi" | "markdown";

const copyLabels: Record<Language, { copied: string; copy: string }> = {
  en: { copied: "Copied", copy: "Copy" },
  ko: { copied: "복사됨", copy: "복사" }
};

const text = {
  en: {
    nav: ["Preflight", "Records", "Generate", "Policy"],
    eyebrow: "Email sender DNS preflight",
    title: "SendReady DNS",
    subtitle: "Check public DNS before a domain sends marketing, product, or transactional email.",
    domain: "Domain",
    selector: "DKIM selectors",
    selectorHint: "google, selector1, k1",
    check: "Check",
    sample: "Sample",
    score: "Readiness",
    checklist: "Gmail & Yahoo DNS checklist",
    records: "DNS records",
    analysis: "Protocol analysis",
    generated: "DNS change package",
    route: "Mail route",
    recs: "Key recommendations",
    localNote: "DNS only. Inbox placement, spam rate, unsubscribe headers, and sender reputation are outside this check.",
    policy: "DMARC policy",
    monitor: "Monitor",
    quarantine: "Quarantine",
    reject: "Reject",
    submitError: "The check could not run.",
    disclaimer: "Independent tool. Not affiliated with Google, Yahoo, mailbox providers, or any ESP."
  },
  ko: {
    nav: ["점검", "레코드", "생성", "정책"],
    eyebrow: "이메일 발송 DNS 사전 점검",
    title: "SendReady DNS",
    subtitle: "마케팅, 서비스, 트랜잭션 이메일 발송 전 공개 DNS 준비 상태를 확인합니다.",
    domain: "도메인",
    selector: "DKIM 셀렉터",
    selectorHint: "google, selector1, k1",
    check: "점검",
    sample: "샘플",
    score: "준비도",
    checklist: "Gmail & Yahoo DNS 체크리스트",
    records: "DNS 레코드",
    analysis: "프로토콜 분석",
    generated: "DNS 변경 패키지",
    route: "메일 경로",
    recs: "핵심 권장사항",
    localNote: "DNS 전용 점검입니다. 받은편지함 도달률, 스팸률, 구독해지 헤더, 발신 평판은 별도 확인이 필요합니다.",
    policy: "DMARC 정책",
    monitor: "모니터링",
    quarantine: "격리",
    reject: "거부",
    submitError: "점검을 실행하지 못했습니다.",
    disclaimer: "독립 도구이며 Google, Yahoo, 메일 제공업체, ESP와 관련이 없습니다."
  }
};

const outputMeta: Array<{ key: OutputKey; label: string }> = [
  { key: "dmarc", label: "DMARC" },
  { key: "spf", label: "SPF" },
  { key: "mtaStsTxt", label: "MTA-STS TXT" },
  { key: "mtaStsPolicy", label: "MTA-STS file" },
  { key: "tlsRpt", label: "TLS-RPT" },
  { key: "bimi", label: "BIMI" },
  { key: "markdown", label: "Markdown" }
];

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [domain, setDomain] = useState("example.com");
  const [selector, setSelector] = useState("selector1");
  const [policy, setPolicy] = useState<PolicyMode>("quarantine");
  const [audit, setAudit] = useState<MailAuthAudit>(sampleAudit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeOutput, setActiveOutput] = useState<OutputKey>("markdown");
  const labels = text[language];

  const recordRows = useMemo(() => buildRecordRows(audit), [audit]);
  const outputValue = audit.generated[activeOutput];

  async function runCheck(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        domain,
        selector,
        policy
      });
      const response = await fetch(`/api/check?${params.toString()}`);
      const payload = (await response.json()) as MailAuthAudit | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? labels.submitError : labels.submitError);
      }
      setAudit(payload as MailAuthAudit);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : labels.submitError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" className="brand" href="/" aria-label="SendReady DNS home">
          <span className="brand-mark">
            <ShieldCheck size={23} />
          </span>
          <span>{labels.title}</span>
        </a>
        <nav aria-label="Primary">
          {labels.nav.map((item) => (
            <a data-google-vignette="false" href={`#${item.toLowerCase()}`} key={item}>
              {item}
            </a>
          ))}
        </nav>
        <button
          className="icon-button language-button"
          type="button"
          onClick={() => setLanguage(language === "en" ? "ko" : "en")}
          aria-label="Change language"
        >
          <Languages size={18} />
          <span>{language.toUpperCase()}</span>
        </button>
      </header>

      <main>
        <section className="intro-grid">
          <div className="intro-copy">
            <p className="eyebrow">{labels.eyebrow}</p>
            <h1>{labels.title}</h1>
            <p>{labels.subtitle}</p>
          </div>
          <div className="visual-strip" aria-label="SendReady DNS product preview">
            <img src="/sendready-console.png" alt="SendReady DNS interface preview" />
          </div>
        </section>

        <form className="scan-bar" onSubmit={runCheck}>
          <label>
            <span>{labels.domain}</span>
            <input value={domain} onChange={(event) => setDomain(event.target.value)} spellCheck={false} inputMode="url" />
          </label>
          <label>
            <span>{labels.selector}</span>
            <input value={selector} onChange={(event) => setSelector(event.target.value)} placeholder={labels.selectorHint} spellCheck={false} />
          </label>
          <fieldset className="policy-group" aria-label={labels.policy}>
            <legend>{labels.policy}</legend>
            {(["monitor", "quarantine", "reject"] as const).map((mode) => (
              <button key={mode} type="button" className={policy === mode ? "active" : ""} onClick={() => setPolicy(mode)}>
                {labels[mode]}
              </button>
            ))}
          </fieldset>
          <div className="scan-actions">
            <button className="secondary-button" type="button" onClick={() => setAudit(sampleAudit)}>
              {labels.sample}
            </button>
            <button className="primary-button" type="submit" disabled={loading}>
              {loading ? <RefreshCw size={18} className="spin" /> : <Search size={18} />}
              {labels.check}
            </button>
          </div>
        </form>

        {error ? (
          <div className="error-banner" role="alert">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        ) : null}

        <section className="dashboard-grid">
          <aside className="left-stack">
            <Panel title={labels.score} icon={<ShieldCheck size={18} />}>
              <ScoreRing score={audit.score} label={audit.scoreLabel} />
              <div className="timestamp">Checked {formatDate(audit.checkedAt)}</div>
            </Panel>

            <Panel title={labels.checklist} icon={<MailCheck size={18} />}>
              <ul className="checklist">
                {audit.checklist.map((item) => (
                  <ChecklistRow item={item} key={item.key} />
                ))}
              </ul>
              <p className="note">{labels.localNote}</p>
            </Panel>
          </aside>

          <section className="center-stack">
            <Panel title={labels.records} icon={<ServerCog size={18} />} id="records">
              <div className="record-table-wrap">
                <table className="record-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Value</th>
                      <th>Status</th>
                      <th>TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordRows.map((row) => (
                      <tr key={`${row.type}-${row.name}`}>
                        <td>{row.type}</td>
                        <td>{row.name}</td>
                        <td>
                          <code>{row.value}</code>
                        </td>
                        <td>
                          <LevelPill level={row.level} />
                        </td>
                        <td>{row.ttl || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title={labels.analysis} icon={<Clipboard size={18} />}>
              <div className="analysis-grid">
                <AnalysisCard title="SPF" level={audit.spf.status} findings={audit.spf.findings} meta={`${audit.spf.lookupCount}/10 DNS lookups`} />
                <AnalysisCard title="DMARC" level={audit.dmarc.status} findings={audit.dmarc.findings} meta={`p=${audit.dmarc.policy}`} />
                <AnalysisCard title="DKIM" level={audit.dkim.length ? mergeLevels(audit.dkim.map((item) => item.status)) : "info"} findings={audit.dkim.flatMap((item) => item.findings)} meta={audit.dkim.length ? `${audit.dkim.length} selector(s)` : "selectors optional"} />
                <AnalysisCard title="MTA-STS" level={audit.mtaSts.status} findings={audit.mtaSts.findings} meta={audit.mtaSts.policyFields.mode ?? "not published"} />
                <AnalysisCard title="TLS-RPT" level={audit.tlsRpt.status} findings={audit.tlsRpt.findings} meta={audit.tlsRpt.rua || "no rua"} />
                <AnalysisCard title="BIMI" level={audit.bimi.status} findings={audit.bimi.findings} meta={audit.bimi.logoUrl ? "logo URL found" : "optional"} />
              </div>
            </Panel>

            <Panel title={labels.generated} icon={<FileText size={18} />} id="generate">
              <div className="output-tabs" role="tablist" aria-label="Generated DNS records">
                {outputMeta.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={activeOutput === item.key ? "active" : ""}
                    onClick={() => setActiveOutput(item.key)}
                    role="tab"
                    aria-selected={activeOutput === item.key}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="output-box">
                <pre>{outputValue}</pre>
                <CopyButton text={outputValue} language={language} />
              </div>
            </Panel>
          </section>

          <aside className="right-stack">
            <Panel title={labels.route} icon={<Globe2 size={18} />}>
              <MailRoute audit={audit} />
            </Panel>

            <Panel title={labels.recs} icon={<AlertTriangle size={18} />}>
              <ul className="recommendations">
                {audit.recommendations.map((item) => (
                  <li key={`${item.title}-${item.action}`}>
                    <LevelDot level={item.level} />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.action}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          </aside>
        </section>
      </main>

      <footer>
        <span>{labels.disclaimer}</span>
        <a data-google-vignette="false" href="/privacy.html">Privacy</a>
        <a data-google-vignette="false" href="/about.html">About</a>
      </footer>
    </div>
  );
}

function Panel({ title, icon, children, id }: { title: string; icon: React.ReactNode; children: React.ReactNode; id?: string }) {
  return (
    <section className="panel" id={id}>
      <div className="panel-heading">
        <span>{icon}</span>
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ScoreRing({ score, label }: { score: number; label: string }) {
  const angle = Math.max(0, Math.min(100, score)) * 3.6;
  return (
    <div className="score-ring" style={{ "--score-angle": `${angle}deg` } as React.CSSProperties}>
      <div className="score-ring-inner">
        <strong>{score}</strong>
        <span>/100</span>
      </div>
      <p>{label}</p>
    </div>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  return (
    <li>
      <StatusIcon level={item.level} />
      <div>
        <strong>{item.label}</strong>
        <span>{item.detail}</span>
      </div>
    </li>
  );
}

function AnalysisCard({ title, level, findings, meta }: { title: string; level: CheckLevel; findings: Finding[]; meta: string }) {
  const visible = findings.length ? findings.slice(0, 3) : [{ level: "info" as const, title: "No signal", detail: "No findings were returned." }];
  return (
    <article className="analysis-item">
      <div className="analysis-head">
        <div>
          <h3>{title}</h3>
          <span>{meta}</span>
        </div>
        <LevelPill level={level} />
      </div>
      <ul>
        {visible.map((item) => (
          <li key={`${title}-${item.title}`}>
            <LevelDot level={item.level} />
            <span>{item.title}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function MailRoute({ audit }: { audit: MailAuthAudit }) {
  const routeItems: Array<{ label: string; detail: string; icon: React.ReactNode; level: CheckLevel }> = [
    { label: audit.domain, detail: "Domain", icon: <Globe2 size={20} />, level: "pass" as CheckLevel },
    { label: "Authoritative DNS", detail: `${audit.checklist.filter((item) => item.level === "pass").length}/${audit.checklist.length} checks`, icon: <ServerCog size={20} />, level: mergeLevels(audit.checklist.map((item) => item.level)) },
    { label: "SMTP transport", detail: audit.mtaSts.policyFields.mode ?? "policy check", icon: <LockKeyhole size={20} />, level: audit.mtaSts.status },
    { label: "Mailbox providers", detail: "Gmail, Yahoo, Outlook", icon: <MailCheck size={20} />, level: audit.score >= 70 ? "pass" : "warn" }
  ];

  return (
    <div className="mail-route">
      {routeItems.map((item, index) => (
        <React.Fragment key={item.label}>
          <div className="route-node">
            <span>{item.icon}</span>
            <div>
              <strong>{item.label}</strong>
              <small>{item.detail}</small>
            </div>
            <LevelPill level={item.level} />
          </div>
          {index < routeItems.length - 1 ? <div className="route-line" /> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function CopyButton({ text: value, language }: { text: string; language: Language }) {
  const [copied, setCopied] = useState(false);
  const labels = copyLabels[language];

  async function copyValue() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button className="copy-button" type="button" onClick={copyValue}>
      <Copy size={16} />
      {copied ? labels.copied : labels.copy}
    </button>
  );
}

function LevelPill({ level }: { level: CheckLevel }) {
  return <span className={`level-pill ${level}`}>{level}</span>;
}

function LevelDot({ level }: { level: CheckLevel }) {
  return <span className={`level-dot ${level}`} aria-hidden="true" />;
}

function StatusIcon({ level }: { level: CheckLevel }) {
  if (level === "pass") return <CheckCircle2 className="status-icon pass" size={18} />;
  if (level === "fail") return <XCircle className="status-icon fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="status-icon warn" size={18} />;
  return <Info className="status-icon info" size={18} />;
}

function mergeLevels(levels: CheckLevel[]): CheckLevel {
  if (levels.includes("fail")) return "fail";
  if (levels.includes("warn")) return "warn";
  if (levels.includes("pass")) return "pass";
  return "info";
}

function buildRecordRows(audit: MailAuthAudit) {
  return [
    {
      type: "TXT",
      name: "@",
      value: audit.spf.records[0] ?? "Not found",
      level: audit.spf.status,
      ttl: audit.queries.spfTxt.answers[0]?.ttl ?? 0
    },
    {
      type: "TXT",
      name: "_dmarc",
      value: audit.dmarc.records[0] ?? "Not found",
      level: audit.dmarc.status,
      ttl: audit.queries.dmarcTxt.answers[0]?.ttl ?? 0
    },
    {
      type: "MX",
      name: "@",
      value: audit.mx.hosts.map((item) => `${item.priority} ${item.host}`).join(", ") || "Not found",
      level: audit.mx.status,
      ttl: audit.queries.mx.answers[0]?.ttl ?? 0
    },
    {
      type: "TXT",
      name: "_mta-sts",
      value: audit.mtaSts.txtRecords[0] ?? "Not found",
      level: audit.mtaSts.status,
      ttl: audit.queries.mtaStsTxt.answers[0]?.ttl ?? 0
    },
    {
      type: "TXT",
      name: "_smtp._tls",
      value: audit.tlsRpt.records[0] ?? "Not found",
      level: audit.tlsRpt.status,
      ttl: audit.queries.tlsRptTxt.answers[0]?.ttl ?? 0
    },
    {
      type: "TXT",
      name: "default._bimi",
      value: audit.bimi.records[0] ?? "Not found",
      level: audit.bimi.status,
      ttl: audit.queries.bimiTxt.answers[0]?.ttl ?? 0
    }
  ];
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(value));
  } catch {
    return value;
  }
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
